// tslint:disable: no-console
import {
  formatBytes,
  getKernelsFirestoreClient,
  KernelsFirestoreClient,
  RunStatus,
  StdStreamDispatcher,
} from "@kaggle/components";
import { featureFlags, Kaggle } from "@kaggle/sdk";
import {
  collection,
  doc,
  DocumentChange,
  DocumentSnapshot,
  getFirestore,
  onSnapshot,
  QuerySnapshot,
} from "firebase/firestore";
import { getLogger } from "../../../api/getServices";
import { kernelsClient } from "../../../api/services";
import { editorFrameClient } from "../../pages/editor/editorFrameClient";
import { getKernelVersions } from "../../session/apiClient";
import { isTpuAccelerator } from "../../types/Accelerator";
import { EditorStatus } from "../../types/EditorStatus";
import { ResourceUsage } from "../../types/entities/run";
import { LifecycleStage } from "../../types/LifecycleLatencyStats";
import { getRecentKeepAliveOutcomes } from "../middleware/keepAliveMiddleware";
import { getActiveEventsOpen } from "../selectors/activeEventsOpen";
import { getBatchedRunVersionUrl } from "../selectors/batch";
import {
  getCurrentEditorStatus,
  getCurrentKernelRunId,
  getCurrentRemovedDataSource,
} from "../selectors/current";
import { getAccelerator } from "../selectors/notebook";
import {
  getInitialEpochTime,
  getSessionTimeoutSeconds,
} from "../selectors/timer";
import { getStore } from "../store/appStore";
import { editorQueue } from "../utils/editorSnackbarQueue";
import {
  putBatchedRun,
  removeBatchedRun,
  setBatchedRunErrorMessage,
} from "./batchedRun";
import {
  editorErrorOccurred,
  setEditorStatus,
  setNonfatalErrorMessage,
  userErrorOccurred,
} from "./editor";
import { reportLifecycleChange } from "./lifecycle";
import {
  hideModal,
  showCommitDemandModal,
  showEditorIdleTimeOutModal,
} from "./modal";
import {
  setBackendUnitName,
  setFirebaseSessionId,
  setIsInternetEnabledBackend,
  setJupyterProxyUrl,
  setQueuePosition,
  setRemovedDataSource,
} from "./notebook";
import { addDataSource } from "./notebook/addDataSource";
import {
  fetchRunSuccess,
  setRunStatus,
  updateDatasetDownloadProgressMessage,
  updateResourceUsage,
  updateRunLogs,
  updateStdStream,
} from "./runs";
import {
  resetTimer,
  setSessionIdleTimeoutSeconds,
  setSessionTimeoutSeconds,
  startTimer,
} from "./timer";
import KernelVersionType = Kaggle.Sdk.Kernels.KernelVersionType;

const OOM_ERROR_MESSAGE =
  "Your notebook tried to allocate too much memory and could not be recovered. You will need to start a new session.";

const OOD_ERROR_MESSAGE =
  "Your notebook wrote too much data outside /kaggle/working. You will need to start a new session.";

const STREAM_END = "END_OF_LOG";

let firestoreClient: Promise<EditorFirestoreClient>;
let firestoreClientLoaded: EditorFirestoreClient;

export const getFirestoreClient = async () => {
  if (!firestoreClient) {
    firestoreClient = getKernelsFirestoreClient(
      app => new EditorFirestoreClient(app),
      editorErrorOccurred
    ) as Promise<EditorFirestoreClient>;
    firestoreClient.then(cli => (firestoreClientLoaded = cli));
  }
  return firestoreClient;
};

export const stopListeners = () => {
  if (firestoreClientLoaded) {
    firestoreClientLoaded.stopAllFirestoreListeners();
  }
};

class EditorFirestoreClient extends KernelsFirestoreClient {
  protected onError(err: Error) {
    editorErrorOccurred(err.message);
  }

  getSessionDocument = (sessionId: string) =>
    doc(collection(getFirestore(this.app), "sessions"), sessionId);

  getDataCollection = (sessionId: string) =>
    collection(this.getSessionDocument(sessionId), "data");

  getStatusDocument = (sessionId: string) =>
    doc(this.getDataCollection(sessionId), "Status");

  getLogsUrlDocument = (sessionId: string) =>
    doc(this.getDataCollection(sessionId), "LogsURL");

  getQueueInfoDocument = (sessionId: string) =>
    doc(this.getDataCollection(sessionId), "QueueInfo");

  getContainerSettingsDocument = (sessionId: string) =>
    doc(this.getDataCollection(sessionId), "ContainerSettings");

  getEventsCollection = (sessionId: string) =>
    collection(this.getSessionDocument(sessionId), "Event");

  getDatasourceCollection = (sessionId: string) =>
    collection(this.getSessionDocument(sessionId), "MountDataset");

  getJupyterUrlDocument = (sessionId: string) =>
    doc(this.getDataCollection(sessionId), "JupyterURL");

  getResourceDocument = (sessionId: string) =>
    doc(this.getDataCollection(sessionId), "ResourceUsage");

  setJupyterUrlListener = async (sessionId: string) => {
    this.unsubscribeFunctions.push(
      onSnapshot(
        this.getJupyterUrlDocument(sessionId),
        async (jupyterUrlDoc: DocumentSnapshot) => {
          try {
            const url = jupyterUrlDoc.get("url");
            if (url && url.length > 0) {
              setJupyterProxyUrl(url);

              reportLifecycleChange(
                LifecycleStage.JUPYTER_URL_RECEIVED,
                Date.now()
              );
            }
          } catch (ex) {
            editorErrorOccurred("Failed to connect to Jupyter: " + ex.message);
          }
        }
      )
    );
  };

  setResourceMonitoringListener = (sessionId: string) => {
    const { getState } = getStore();

    // If system disk (not /kaggle/working) usage exceeds 60% we run
    // the risk of running out of system disk and crashing the host.
    // This will trigger a warning to the user before this happens.
    // It will trigger *once* everytime usage goes from under this
    // level to over this threshold, OR if the page is refreshed.
    const highDiskUsageAlertPct = 0.6;
    let highDiskUsageAlerted = false;

    // We spawn the idle timeout modal a couple minutes before the actual
    // idle timeout would occur to give the user a change to manually
    // trigger a keepalive.
    const idleTimeoutAlertSeconds = 2 * 60;
    let idleTimeoutAlerted = false;

    try {
      this.unsubscribeFunctions.push(
        onSnapshot(
          this.getResourceDocument(sessionId),
          (resourceDoc: DocumentSnapshot) => {
            const runId = getCurrentKernelRunId(getState());
            if (runId && resourceDoc.exists()) {
              const initialEpochTime = getInitialEpochTime(getState());
              if (!initialEpochTime) {
                startTimer(parseStartTimeIntoEpoch(resourceDoc));
                setSessionTimeoutSeconds(
                  parseInt(resourceDoc.get("maxExecutionTime") || 0, 10)
                );
                setSessionIdleTimeoutSeconds(
                  parseInt(resourceDoc.get("maxIdleTime") || 0, 10)
                );
              }

              const secondsRemaining = parseIdleTimeRemaining(resourceDoc);
              if (
                !idleTimeoutAlerted &&
                secondsRemaining < idleTimeoutAlertSeconds &&
                secondsRemaining > 0
              ) {
                idleTimeoutAlerted = true;
                showEditorIdleTimeOutModal();
              } else if (
                idleTimeoutAlerted &&
                secondsRemaining > idleTimeoutAlertSeconds
              ) {
                idleTimeoutAlerted = false;
              }

              const resources = parseResourceUsageFromDocument(resourceDoc);

              // Decide if we need to alert the user on high system disk usage:
              const hostDiskUsage =
                resources.hostDisk.limit > 0
                  ? resources.hostDisk.usage / resources.hostDisk.limit
                  : 0;
              if (
                hostDiskUsage > highDiskUsageAlertPct &&
                !highDiskUsageAlerted
              ) {
                highDiskUsageAlerted = true;
                editorQueue.notify({
                  title: `You're approaching the limit of disk space outside /kaggle/working, continued use may cause your notebook to crash.`,
                  actions: [{ icon: "close" }],
                  timeout: 10000, // Longer wait since the message is long.
                });
              } else if (
                hostDiskUsage < highDiskUsageAlertPct &&
                highDiskUsageAlerted
              ) {
                highDiskUsageAlerted = false;
              }

              updateResourceUsage(runId, resources);
            }
          }
        )
      );
    } catch {
      throw new Error("Could not set resource monitoring.");
    }
  };

  setQueueInfoListener = (sessionId: string) => {
    try {
      onSnapshot(
        this.getQueueInfoDocument(sessionId),
        (queueInfoDoc: DocumentSnapshot) => {
          const positionText = queueInfoDoc.get("position");
          const position = positionText && JSON.parse(positionText);
          if (typeof position === "number" && position >= 1) {
            setQueuePosition(position);
          }
        }
      );
    } catch {
      throw new Error("Could not retrieve the queue info document");
    }
  };

  setDatasourceDownloadProgressListener = (sessionId: string) => {
    const { getState } = getStore();
    try {
      this.unsubscribeFunctions.push(
        onSnapshot(
          this.getDatasourceCollection(sessionId),
          (snapshot: QuerySnapshot) => {
            const state = getState();
            const runId = getCurrentKernelRunId(state);
            const messages: string[] = [];
            if (runId) {
              snapshot.docChanges().forEach((change: DocumentChange) => {
                const datasourceProgressDoc = change.doc;
                const datasourceProgressMessage =
                  parseDatasourceProgressDocument(datasourceProgressDoc);
                const { mountStatus, displayName } = datasourceProgressMessage;
                const datasetTitle = displayName || "Your dataset";
                const undoDatasetRemoval = () => {
                  const localState = getState();
                  const removedDataSource =
                    getCurrentRemovedDataSource(localState);
                  if (removedDataSource != null) {
                    addDataSource([removedDataSource]);
                  }
                  setRemovedDataSource(null);
                };
                switch (mountStatus) {
                  case MountStatus.DOWNLOADING:
                    messages.push(
                      getDatasourceDownloadingMessage(
                        datasetTitle,
                        datasourceProgressMessage.filesCopied,
                        datasourceProgressMessage.totalFilesToCopy,
                        datasourceProgressMessage.bytesCopied,
                        datasourceProgressMessage.totalBytesToCopy
                      )
                    );
                    break;

                  case MountStatus.READY:
                    const isRunning =
                      getCurrentEditorStatus(getState()) ===
                      EditorStatus.RUNNING;
                    if (isRunning) {
                      editorQueue.notify({
                        title: `${datasetTitle} is ready to use.`,
                        actions: [{ icon: "close" }],
                        timeout: 4000,
                        leading: true,
                      });
                    }
                    break;

                  case MountStatus.UNMOUNTED:
                    editorQueue.notify({
                      title: `${datasetTitle} was removed.`,
                      actions: [{ label: "Undo", onClick: undoDatasetRemoval }],
                      timeout: 4000,
                    });
                    break;
                }
              });

              updateDatasetDownloadProgressMessage(runId, messages);
            }
          }
        )
      );
    } catch {
      throw new Error(
        "Could not get set download progress listening for data sources."
      );
    }
  };

  setContainerSettingsListener = (sessionId: string) => {
    const { getState } = getStore();
    this.unsubscribeFunctions.push(
      onSnapshot(
        this.getContainerSettingsDocument(sessionId),
        async (containerSettingsDoc: DocumentSnapshot) => {
          try {
            if (containerSettingsDoc.exists()) {
              const isInternetEnabled =
                containerSettingsDoc.get("isInternetEnabled");
              const unitName = containerSettingsDoc.get("unitName");
              setBackendUnitName(unitName);

              const state = getState();
              const runId = getCurrentKernelRunId(state);
              if (runId && state.editor.status === EditorStatus.RUNNING) {
                editorQueue.notify({
                  title: `Internet is ${
                    isInternetEnabled ? "enabled" : "disabled"
                  }.`,
                  actions: [{ icon: "close" }],
                  timeout: 4000,
                  leading: true,
                });
              }
              setIsInternetEnabledBackend(isInternetEnabled);
            }
          } catch (ex) {
            editorErrorOccurred(
              "Failed to get container settings: " + ex.message
            );
          }
        }
      )
    );
  };

  setAllFirestoreListeners = async (runId: number) => {
    try {
      const sessionId = await this.getKernelFirestoreSessionId(runId);
      setFirebaseSessionId(sessionId);
      reportLifecycleChange(LifecycleStage.FIREBASE_CONNECTED, Date.now());

      if (sessionId != null) {
        this.listenAll(sessionId);
      }
    } catch (ex) {
      editorErrorOccurred(ex.message);
    }
  };

  listenAll(sessionId: string) {
    this.setJupyterUrlListener(sessionId);
    this.setResourceMonitoringListener(sessionId);
    this.setQueueInfoListener(sessionId);
    this.setDatasourceDownloadProgressListener(sessionId);
    this.setInteractiveSessionStatusListener(sessionId);
    this.setInteractiveSessionEventListener(sessionId);
    this.setContainerSettingsListener(sessionId);
  }

  stopAllFirestoreListeners = () => {
    this.unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    this.unsubscribeFunctions = [];
  };

  resetAllFirestoreListeners = async (runId: number) => {
    try {
      const sessionId = await this.getKernelFirestoreSessionId(runId);
      setFirebaseSessionId(sessionId);
      this.stopAllFirestoreListeners();
      resetTimer();

      if (sessionId != null) {
        this.listenAll(sessionId);
      }
    } catch (ex) {
      editorErrorOccurred(ex.message);
    }
  };

  setInteractiveSessionStatusListener = (sessionId: string) => {
    const { getState } = getStore();
    const unsubscribeFunction = onSnapshot(
      this.getStatusDocument(sessionId),
      async (statusDoc: DocumentSnapshot) => {
        const status: SessionStatus = Number(statusDoc.get("status") || 0);
        const doneReason: DoneReason = Number(statusDoc.get("doneReason") || 0);
        const blame: BlameParty = Number(statusDoc.get("blame") || 0);

        let message = "";

        console.log(
          `Firestore session ${sessionId} status ${SessionStatus[status]}`
        );

        // Handle completion states
        if (status === SessionStatus.CLOSING || status === SessionStatus.DONE) {
          // We're stopping/stopped, stop the kernel inside the frame so that it stops
          // trying to talk to the backend server and clears its websocket state.
          // Otherwise it's possible that trying to create a new connection will get
          // confused about which backend server to connect to.
          editorFrameClient.shutdownKernel();

          if (doneReason === DoneReason.CANCELED_IDLE_TIMEOUT) {
            // Get the list of recent keep-alive outcomes and run id for analytics (prior to redux updates).
            const keepAliveOutcomes = getRecentKeepAliveOutcomes();
            const kernelRunId = getCurrentKernelRunId(getState());
            const accelerator = getAccelerator(getState());

            setEditorStatus(EditorStatus.STOPPED);
            showEditorIdleTimeOutModal();

            // Report the recent keep alive outcomes for debugging how we got here in problem cases.
            getLogger().report("kernels", "editor_time_out", {
              kernelRunId,
              accelerator,
              firebase: {
                sessionId,
                status,
                doneReason,
                blame,
              },
              keepAliveOutcomes,
            });
            return;
          } else if (doneReason === DoneReason.ERRORED_OUT_OF_MEMORY) {
            message = OOM_ERROR_MESSAGE;
          } else if (doneReason === DoneReason.ERRORED_OUT_OF_SYSTEM_DISK) {
            message = OOD_ERROR_MESSAGE;
          } else if (doneReason === DoneReason.CANCELED_REQUEST) {
            // No message needed when stopping a session with sessionless editing.
            setEditorStatus(EditorStatus.STOPPED);
            return;
          } else if (doneReason === DoneReason.CANCELED_EXECUTION_TIMEOUT) {
            const timeoutSeconds = getSessionTimeoutSeconds(getState()) || 0;
            let timeMessage = `${timeoutSeconds / 3600} hours`;
            if (timeoutSeconds < 3600) {
              timeMessage = `${timeoutSeconds / 60} minutes`;
            }
            message = `Are you still there? Your notebook stops after ${timeMessage} of continuous use.`;
          }

          // Make sure we always provide a message.
          if (message === "") {
            const errorMessage =
              statusDoc.get("errorMessage") ||
              "unknown error, status code " + doneReason;
            message = "Your session ended with an error: " + errorMessage;
          }

          if (blame === BlameParty.USER) {
            userErrorOccurred(message);
          } else {
            editorErrorOccurred(message);
            setEditorStatus(EditorStatus.FATAL);
          }
        }
      },
      (error: Error) => {
        if (error.message.includes("Missing or insufficient permissions")) {
          // A couple days after a notebook is no longer in use, we delete
          // its records in Firestore. This means that Firestore Security
          // rules no longer know this user had access to this notebook and
          // therefore returns that the user has no permission to view the
          // non-existant session record. Since this is happening days
          // after the notebook was in use, we should treat it as if it was
          // an idle timeout.
          getLogger().report("kernels", "firestore-missing-permissions");
          setEditorStatus(EditorStatus.STOPPED);
          showEditorIdleTimeOutModal();
          return;
        }
        const message = "Connection to the backend failed: " + error.message;
        editorErrorOccurred(message);
      }
    );
    this.unsubscribeFunctions.push(unsubscribeFunction);
  };

  setInteractiveSessionEventListener = (sessionId: string) => {
    let invocationCount = 0;
    const unsubscribeFunction = onSnapshot(
      this.getEventsCollection(sessionId),
      (snapshot: QuerySnapshot) => {
        invocationCount += 1;
        if (invocationCount === 1) {
          // We have to ignore the first invocation of onSnapshot which provides the original state
          // because we only want to handle newly added events.
          return;
        }
        snapshot.docChanges().forEach(change => {
          if (change.type === "added") {
            const eventType = change.doc.get("eventType");
            switch (eventType) {
              case EventType.EVENT_TYPE_OOM:
                setNonfatalErrorMessage(
                  "Your notebook tried to allocate more memory than is available. It has restarted."
                );
                break;
              case EventType.EVENT_TYPE_HOST_MAINTENANCE:
                setNonfatalErrorMessage(
                  "This machine is restarting for maintenance within the hour. Consider restarting or committing your notebook."
                );
                break;
            }
          }
        });
      }
    );

    this.unsubscribeFunctions.push(unsubscribeFunction);
  };

  setBatchedRunListener = async (
    runId: number,
    sequence: number,
    versionType: KernelVersionType.BATCH | KernelVersionType.QUICK,
    accelerator: Kaggle.Sdk.Kernels.AcceleratorType
  ) => {
    const { getState } = getStore();

    // Add new run to run entities.
    fetchRunSuccess(runId);
    putBatchedRun(runId, sequence, versionType);

    const sessionId = await this.getKernelFirestoreSessionId(runId);
    if (!sessionId) {
      setBatchedRunErrorMessage("Could not create commit. Try again.");
      return;
    }

    let queuedTimeout = window.setTimeout(() => {
      /*no-op*/
    });

    onSnapshot(
      this.getStatusDocument(sessionId),
      async (statusDoc: DocumentSnapshot) => {
        const sessionStatus: SessionStatus = Number(
          statusDoc.get("status") || 0
        );
        const doneStatus: DoneStatus = Number(statusDoc.get("doneStatus") || 0);
        const doneReason: DoneReason = Number(statusDoc.get("doneReason") || 0);

        const wasCommitDemandModalShown = getState().modal.commitDemand;
        const mayShowCommitDemandModal: boolean =
          !wasCommitDemandModalShown &&
          ((featureFlags.tpuCommitScheduling &&
            isTpuAccelerator(accelerator)) ||
            (featureFlags.commitScheduling && !isTpuAccelerator(accelerator)));

        switch (sessionStatus) {
          case SessionStatus.QUEUED:
            if (mayShowCommitDemandModal) {
              // If the session is queued for more than 10 seconds, an explanatory modal appears.
              // Queued duration almost never reaches 10 seconds at P99 (http://shortn/_CpB7RbqTkW).
              queuedTimeout = window.setTimeout(() => {
                getLogger().report("kernels", "commit-demand-modal-open");
                showCommitDemandModal();
              }, 10000);
            }
            break;
          case SessionStatus.EXECUTING:
            setRunStatus(runId, RunStatus.RUNNING);
            if (mayShowCommitDemandModal) {
              clearTimeout(queuedTimeout);
              hideModal();
            }
            break;
          case SessionStatus.DONE:
            switch (doneStatus) {
              case DoneStatus.COMPLETED:
                if (
                  doneReason === DoneReason.COMPLETED_SUCCESS ||
                  doneReason === DoneReason.CANCELED_REQUEST
                ) {
                  setRunStatus(runId, RunStatus.COMPLETE);
                } else {
                  setRunStatus(runId, RunStatus.ERROR);
                }
                break;
              case DoneStatus.CANCELED:
                setRunStatus(runId, RunStatus.CANCEL_ACKNOWLEDGED);
                break;
            }

            if (doneStatus !== DoneStatus.DONE_STATUS_UNSPECIFIED) {
              // Wait 8 seconds before removing the batched run status.
              window.setTimeout(() => {
                removeBatchedRun();
              }, 8000);

              // If the active events window is closed, show a snackbar that the commit is done.
              const state = getState();
              if (!getActiveEventsOpen(state)) {
                const url = getBatchedRunVersionUrl(state);

                editorQueue.notify({
                  title:
                    versionType === KernelVersionType.QUICK
                      ? "Quick version complete."
                      : "Save & Run All complete.",
                  actions: [
                    {
                      title: "View",
                      onClick: () => {
                        openUrlInNewTab(url);
                      },
                    },
                  ],
                  timeout: 10000,
                  leading: true,
                });
              }

              const { logs } =
                await kernelsClient.kernelsService.listKernelRunLogs({
                  kernelRunId: runId,
                });
              updateRunLogs(runId, logs);
              // Add a short sleep to allow the mid-tier to update its version
              // information based on the firebase status.
              await new Promise(resolve => window.setTimeout(resolve, 1000));
              await getKernelVersions();
            }
        }
      }
    );

    const streamDispatcher = new StdStreamDispatcher(runId, updateStdStream);
    onSnapshot(
      this.getLogsUrlDocument(sessionId),
      (logsUrlDoc: DocumentSnapshot) => {
        const url = logsUrlDoc.get("url");
        if (url) {
          const src = new EventSource(url);
          src.addEventListener("message", evt => {
            if (evt.data === STREAM_END) {
              src.close();
              return;
            }
            const data = JSON.parse(evt.data);
            const streamType =
              data.stream_name === "stdout"
                ? "streams:stdout"
                : "streams:stderr";
            streamDispatcher.addLine(streamType, {
              key: data.time,
              value: data.data,
            });
          });
        }
      }
    );
  };
}

const openUrlInNewTab = (url: string | null) => {
  if (url) {
    getLogger().report("kernels", "editor-commit-snackbar-clicked-view");
    window.open(url, "_blank");
  }
};

const parseStartTimeIntoEpoch = (resourceDoc: DocumentSnapshot): number => {
  const startTimeString = resourceDoc.get("executionStartTime");
  const startTime = startTimeString ? new Date(startTimeString) : new Date();
  return startTime.getTime() / 1000;
};

// parseIdleTimeRemaining returns seconds remaining before the session will idle timeout.
// Negative values mean either the timeout has already passed, or we don't know the timeout.
const parseIdleTimeRemaining = (resourceDoc: DocumentSnapshot): number => {
  const idleTimeoutString = resourceDoc.get("idleTimeoutTime");
  if (!idleTimeoutString) {
    return -1;
  }
  const idleTimeout = new Date(idleTimeoutString);
  const now = new Date();
  return (idleTimeout.getTime() - now.getTime()) / 1000;
};

const parseResourceUsageFromDocument = (
  resourceDoc: DocumentSnapshot
): ResourceUsage => {
  const cpu = { pct: Number(resourceDoc.get("cpuPct") || 0) };
  const ram = {
    usage: Number(resourceDoc.get("ramBytes") || 0),
    limit: Number(resourceDoc.get("ramLimitBytes") || 0),
  };
  const disk = {
    usage: Number(resourceDoc.get("diskBytes") || 0),
    limit: Number(resourceDoc.get("diskLimitBytes") || 0),
  };
  const gpu = { pct: Number(resourceDoc.get("gpuPct") || 0) };
  const gpuMemory = {
    usage: Number(resourceDoc.get("gpuMemoryBytes") || 0),
    limit: Number(resourceDoc.get("gpuMemoryLimitBytes") || 0),
  };
  const tpu = { pct: Number(resourceDoc.get("tpuMxuPct") || 0) };
  const tpuIdle = { pct: Number(resourceDoc.get("tpuIdlePct") || 100) };
  const acceleratorEverUtilized = {
    everUtilized: Boolean(resourceDoc.get("acceleratorEverUtilized")),
  };
  const hostDisk = {
    usage: Number(resourceDoc.get("hostDiskBytes") || 0),
    limit: Number(resourceDoc.get("hostDiskLimitBytes") || 0),
  };
  return {
    cpu,
    ram,
    disk,
    hostDisk,
    gpu,
    gpuMemory,
    tpu,
    tpuIdle,
    acceleratorEverUtilized,
  };
};

const parseDatasourceProgressDocument = (
  datasourceProgressDoc: DocumentSnapshot
) => {
  const status = datasourceProgressDoc.get("status");
  const mountStatus = datasourceProgressDoc.get("mountStatus");
  const errorMessage = datasourceProgressDoc.get("errorMessage");
  const displayName = datasourceProgressDoc.get("displayName");
  const bytesCopied = datasourceProgressDoc.get("bytesCopied");
  const filesCopied = datasourceProgressDoc.get("filesCopied");
  const totalFilesToCopy = datasourceProgressDoc.get("totalFilesToCopy");
  const totalBytesToCopy = datasourceProgressDoc.get("totalBytesToCopy");
  return {
    status,
    mountStatus,
    errorMessage,
    displayName,
    bytesCopied,
    filesCopied,
    totalFilesToCopy,
    totalBytesToCopy,
  };
};

const getDatasourceDownloadingMessage = (
  datasourceName: string,
  filesCopied: string,
  totalFilesToCopy: string,
  bytesCopied: string,
  totalBytesToCopy: string
): string => {
  const bytesCopiedFormatted = formatBytes(Number.parseInt(bytesCopied, 10));
  const totalBytesToCopyFormatted = formatBytes(
    Number.parseInt(totalBytesToCopy, 10)
  );
  if (bytesCopied === "0" && totalBytesToCopy === "0") {
    return `${datasourceName}: Preparing to download`;
  }

  const progress =
    (100 * Number.parseInt(bytesCopied, 10)) /
    Number.parseInt(totalBytesToCopy, 10);
  const progressRounded = Math.floor(progress * 10) / 10;
  return (
    `${datasourceName}: Downloaded ${filesCopied}/${totalFilesToCopy} ` +
    `files: ${bytesCopiedFormatted}/${totalBytesToCopyFormatted} [${progressRounded}%]`
  );
};

// Copied from http://cs/google3/cloud/kaggle/kernels/session/session.proto
enum SessionStatus {
  SESSION_STATUS_UNSPECIFIED = 0,
  NEW = 1, // Session received by SessionScheduler.
  QUEUED = 2, // Session awaiting unit assignment.
  ALLOCATED = 7, // Session has been assigned a unit.
  INITIALIZING = 3, // Session is preparing to execute.
  EXECUTING = 4, // Session has begun executing.
  CLOSING = 5, // Session finished executing.
  DONE = 6, // Session in final state.
}

enum DoneStatus {
  DONE_STATUS_UNSPECIFIED = 0,
  COMPLETED = 1, // Session finished executing.
  CANCELED = 2, // Session was canceled.
  ERRORED = 3, // An infrastructure error occurred.
}

enum DoneReason {
  DONE_REASON_UNSPECIFIED = 0,
  // DoneReason.COMPLETED reasons (reserved 1-20):
  COMPLETED_SUCCESS = 1, // Session completed successfully.
  COMPLETED_FAILURE = 2, // Session completed with a generic failure.
  // TODO(herbison): Include failure reasons:
  // - Used up space
  // - Used up memory

  // DoneReason.CANCELED reasons (reserved 31-50):
  CANCELED_REQUEST = 31, // Session was canceled by a request.
  CANCELED_EXECUTION_TIMEOUT = 32, // Session exceeded execution time.
  CANCELED_IDLE_TIMEOUT = 33, // Session exceeded idle time.
  CANCELED_BLACKLISTED_PROCESS = 34, // Session ran a blacklisted process.

  // DoneReason.ERRORED reasons (reserved 51-70):
  ERRORED_HEARTBEAT_TIMEOUT = 51, // Session exceeded heartbeat time.
  ERRORED_REU_NOT_FOUND = 52, // No REU is allocated to the Session.
  ERRORED_FILESYSTEM_SETUP = 53, // Failed to create the filesystem.
  ERRORED_MOUNTING = 54, // Failed to mount data to container.
  ERRORED_CREATING_CONTAINER = 55, // Failed to create the container.
  ERRORED_STARTING_CONTAINER = 56, // Failed to start the container.
  ERRORED_CONTAINER_STATE = 57, // Container has bad state.
  ERRORED_OUT_OF_MEMORY = 58, // Container was killed due to OOM.
  ERRORED_OUT_OF_DISK = 59, // Container ran out of disk space.
  ERRORED_UPLOADING = 60, // Failed to upload data.
  ERRORED_CREATING_NETWORKS = 61, // Failed to create Container networks.
  ERRORED_REU_RECREATED = 62, // REU was recreated while in use.
  ERRORED_REU_UNREACHABLE = 63, // REU could not be communicated with.
  ERRORED_REU_HOST_MAINTENANCE = 64, // REU terminated for GCP maintenance.
  ERRORED_STARTING_USER_IMAGE = 65, // Failed to start user image
  ERRORED_FIRESTORE = 66, // Failed to talk with Firestore.
  ERRORED_OUT_OF_SYSTEM_DISK = 67, // Host ran out of disk space in /tmp.
}

enum EventType {
  EVENT_TYPE_UNSPECIFIED = 0, // Never set, this value means 'unset'.
  EVENT_TYPE_OOM = 1, // An OOM occurred.
  EVENT_TYPE_HOST_MAINTENANCE = 2, // GCP notified us it plans to do maintenance on this machine within the hour.
}

enum BlameParty {
  BLAME_PARTY_UNSPECIFIED = 0,
  SERVICE = 1,
  USER = 2,
  GCP = 3,
}

enum MountStatus {
  MOUNT_STATUS_UNSPECIFIED = 0, // Never set, this value means 'unset'.
  DOWNLOADING = 1, // The dataset is being downloaded to the dataset cache.
  MOUNTING = 2, // The dataset is being mounted to the Docker container.
  READY = 3, // The dataset is fully mounted and ready for use.
  ERROR = 4, // The dataset failed to be mounted.
  UNMOUNTED = 5, // The dataset was unmounted.
}
